/**
 * Input — 16px font-size on mobile (prevents iOS zoom). Error uses --nx-danger border.
 * Keyboard: native.
 */
import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "./utils/cn";
import { Eye, EyeOff } from "lucide-react";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  error?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  sizeVariant?: "sm" | "md" | "lg";
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, iconStart, iconEnd, sizeVariant = "md", ...props }, ref) => {
    // md default bumped from h-10 (40px) to h-11 (44px) so the input
    // meets the WCAG 2.5.5 AAA mobile tap-target threshold. iOS users
    // hit these with thumbs; 40px was tight. lg (h-12) stays; sm
    // (h-9, 36px) is for dense admin tables where the row height
    // drives the control, not primary form inputs.
    const h = sizeVariant === "sm" ? "h-9" : sizeVariant === "lg" ? "h-12" : "h-11";
    return (
      <div className={cn("relative flex w-full items-center")}>
        {iconStart ? (
          <span className="pointer-events-none absolute left-3 text-[var(--nx-text-dim)]" aria-hidden>
            {iconStart}
          </span>
        ) : null}
        <input
          ref={ref}
          className={cn(
            "w-full rounded-[10px] border bg-[var(--nx-surface)] px-4 text-[16px] text-[var(--nx-text)]",
            "placeholder:text-[var(--nx-text-faint)]",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nx-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--nx-bg)]",
            "transition-colors duration-[180ms]",
            h,
            error ? "border-[var(--nx-danger)]" : "border-[var(--nx-line)] hover:border-[var(--nx-line-strong)]",
            iconStart ? "pl-9" : undefined,
            iconEnd ? "pr-9" : undefined,
            className,
          )}
          aria-invalid={error || undefined}
          {...props}
        />
        {iconEnd ? (
          <span className="pointer-events-none absolute right-3 text-[var(--nx-text-dim)]" aria-hidden>
            {iconEnd}
          </span>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, rows = 4, ...props }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        "w-full rounded-[10px] border bg-[var(--nx-surface)] px-4 py-3 text-[16px] text-[var(--nx-text)]",
        "placeholder:text-[var(--nx-text-faint)]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nx-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--nx-bg)]",
        "transition-colors duration-[180ms]",
        error ? "border-[var(--nx-danger)]" : "border-[var(--nx-line)] hover:border-[var(--nx-line-strong)]",
        className,
      )}
      aria-invalid={error || undefined}
      {...props}
    />
  ),
);
Textarea.displayName = "Textarea";

/**
 * PasswordInput — Input with a working show/hide toggle.
 *
 * The toggle flips `type="password"` ↔ `type="text"`, swaps the icon,
 * and updates the aria-label so screen readers announce the state. The
 * button has explicit `pointer-events-auto` to guard against the L-006
 * class of bug where a decorative overlay eats pointer events.
 *
 * Keyboard: Tab to reach, Enter/Space to toggle (native button behavior).
 */
export type PasswordInputProps = Omit<InputProps, "type" | "iconEnd">;

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, error, iconStart, sizeVariant = "md", ...props }, ref) => {
    const [visible, setVisible] = useState(false);
    // md default bumped from h-10 (40px) to h-11 (44px) so the input
    // meets the WCAG 2.5.5 AAA mobile tap-target threshold. iOS users
    // hit these with thumbs; 40px was tight. lg (h-12) stays; sm
    // (h-9, 36px) is for dense admin tables where the row height
    // drives the control, not primary form inputs.
    const h = sizeVariant === "sm" ? "h-9" : sizeVariant === "lg" ? "h-12" : "h-11";
    return (
      <div className={cn("relative flex w-full items-center")}>
        {iconStart ? (
          <span className="pointer-events-none absolute left-3 text-[var(--nx-text-dim)]" aria-hidden>
            {iconStart}
          </span>
        ) : null}
        <input
          ref={ref}
          type={visible ? "text" : "password"}
          className={cn(
            "w-full rounded-[10px] border bg-[var(--nx-surface)] px-4 text-[16px] text-[var(--nx-text)]",
            "placeholder:text-[var(--nx-text-faint)]",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nx-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--nx-bg)]",
            "transition-colors duration-[180ms]",
            h,
            error ? "border-[var(--nx-danger)]" : "border-[var(--nx-line)] hover:border-[var(--nx-line-strong)]",
            iconStart ? "pl-9" : undefined,
            "pr-11",
            className,
          )}
          aria-invalid={error || undefined}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          data-password-toggle
          className={cn(
            // 44×44 tap target (Phase 14 Loop 5 mobile audit). Icon
            // stays visually 16px; the hit area is the full button
            // rect. The input container is 44px tall so the button
            // aligns centered without pushing chrome.
            "pointer-events-auto absolute right-1 inline-flex h-11 w-11 items-center justify-center",
            "rounded-[8px] text-[var(--nx-text-dim)] hover:text-[var(--nx-text)]",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nx-accent)]",
          )}
        >
          {visible ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";
